import { isAxiosError } from "axios";
import type {
  AddCostInvoiceBatchResponse,
  AddCostInvoiceResponse,
  AddCostInvoiceResponseListResponse,
  CostCurrency,
  CostDetailsResponse,
  CostFile,
  CostInvoiceStatus,
  CostListResponse,
  CostOrder as GeneratedCostOrder,
  CostResponse as GeneratedCostResponse,
  CostStatus,
  DeleteCostAttachmentResponse,
  InvoiceMagicoTwin,
  UpdateCostInvoiceStatusRequest,
  UpdateCostNotesRequest,
  UpdateCostRequest,
} from "@przezmorze/api-types";
import { przezmorzeApi, throwMessageOnBadRequestStatus } from "../../api/api";
import type { NoteDTO } from "../../api/api";
import type { OrderStatus as DomainOrderStatus } from "../data/BaseEntity";

export const costCurrencies: readonly CostCurrency[] = ["EUR", "PLN", "HUF", "CZK", "NOK", "SEK", "DKK", "GBP"];
export const costInvoiceStatus: readonly CostInvoiceStatus[] = [
  "FOR_PAYMENT",
  "PAID",
  "ADVANCE_PAYMENT",
  "EXCESS_PAYMENT",
  "PAYMENT_IN_PROGRESS",
];

export type { CostCurrency, CostInvoiceStatus, CostStatus, InvoiceMagicoTwin };
export type { CostDetailsResponse };
export type { CostFile as FileDTO };
export type { AddCostInvoiceResponse as InvoiceResponse };
export type { UpdateCostRequest };

// order.status/order.price are narrowed here rather than left as the generated wire types: status to the
// frontend's own OrderStatus enum (every other order-status display in the app uses it, see ordersApi.ts for
// the same cast), and price to number | null since a cost's order always has already-placed products with a
// resolved price — toCostOrderResponse below folds the sentinel strings the domain type allows for in general
// (an order awaiting pricing) into null.
export type CostOrderResponse = Omit<GeneratedCostOrder, "status" | "price"> & {
  readonly status: DomainOrderStatus;
  readonly price: number | null;
};

export type CostResponse = Omit<GeneratedCostResponse, "order"> & { readonly order: CostOrderResponse };

function toCostOrderResponse(order: GeneratedCostOrder): CostOrderResponse {
  return {
    ...order,
    status: order.status as DomainOrderStatus,
    price: typeof order.price === "number" ? order.price : null,
  };
}

function toCostResponse(cost: GeneratedCostResponse): CostResponse {
  return { ...cost, order: toCostOrderResponse(cost.order) };
}

export async function listCosts(
  params: { from: string; to?: string; showPaid: boolean },
  abortSignal?: AbortSignal,
): Promise<CostResponse[]> {
  const response = await przezmorzeApi.get<CostListResponse>("/costs", {
    signal: abortSignal,
    params: { from: params.from, to: params.to, showPaid: params.showPaid ? "true" : "false" },
  });
  return response.data.items.map(toCostResponse);
}

export async function getCost(costId: string, abortSignal?: AbortSignal): Promise<CostResponse> {
  const response = await przezmorzeApi.get<GeneratedCostResponse>(`/costs/${encodeURIComponent(costId)}`, {
    signal: abortSignal,
  });
  return toCostResponse(response.data);
}

export async function updateCost(
  costId: string,
  updateRequest: UpdateCostRequest,
  abortSignal?: AbortSignal,
): Promise<CostDetailsResponse> {
  try {
    const response = await przezmorzeApi.put<CostDetailsResponse>(
      `/costs/${encodeURIComponent(costId)}`,
      updateRequest,
      {
        signal: abortSignal,
      },
    );
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function updateCostNotes(
  costId: string,
  notes: NoteDTO[],
  abortSignal?: AbortSignal,
): Promise<CostDetailsResponse> {
  const body: UpdateCostNotesRequest = { notes };
  const response = await przezmorzeApi.put<CostDetailsResponse>(`/costs/${encodeURIComponent(costId)}/notes`, body, {
    signal: abortSignal,
  });
  return response.data;
}

export async function deleteCost(costId: string, abortSignal?: AbortSignal): Promise<void> {
  await przezmorzeApi.delete(`/costs/${encodeURIComponent(costId)}`, { signal: abortSignal });
}

export interface AddInvoiceBatchRequest {
  readonly file: File;
  readonly invoiceNumber: string;
  readonly invoiceDate: string;
  readonly invoiceCurrency: CostCurrency;
  readonly invoiceStatus: CostInvoiceStatus;
  readonly costs: { readonly id: string; readonly value: number; readonly exchangeRate?: number }[];
}

export class InvoiceAttachedToManyCostsError extends Error {
  get name() {
    return "Invoice attached to many costs error";
  }
}

export async function addInvoiceToCosts(
  { file, invoiceNumber, invoiceDate, invoiceCurrency, invoiceStatus, costs }: AddInvoiceBatchRequest,
  abortSignal?: AbortSignal,
): Promise<AddCostInvoiceBatchResponse> {
  const formData = new FormData();
  formData.append("invoiceNumber", invoiceNumber);
  formData.append("date", invoiceDate);
  formData.append("currency", invoiceCurrency);
  formData.append("status", invoiceStatus);
  formData.append("file", file, file.name);
  formData.append("costs", JSON.stringify(costs));
  try {
    const response = await przezmorzeApi.post<AddCostInvoiceBatchResponse>("/cost-invoices", formData, {
      signal: abortSignal,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export type { AddCostInvoiceBatchResponse };

export interface AddInvoiceRequest {
  readonly invoiceNumber: string;
  readonly date: string;
  readonly amount: number;
  readonly exchangeRate?: number;
  readonly status: CostInvoiceStatus;
  readonly file: File;
}

export async function addInvoice(
  costId: string,
  invoice: AddInvoiceRequest,
  abortSignal?: AbortSignal,
): Promise<AddCostInvoiceResponse> {
  const formData = new FormData();
  formData.append("invoiceNumber", invoice.invoiceNumber);
  formData.append("date", invoice.date);
  formData.append("amount", `${invoice.amount}`);
  formData.append("status", invoice.status);
  if (invoice.exchangeRate) {
    formData.append("exchangeRate", `${invoice.exchangeRate}`);
  }
  formData.append("file", invoice.file, invoice.file.name);
  try {
    const response = await przezmorzeApi.post<AddCostInvoiceResponse>(
      `/costs/${encodeURIComponent(costId)}/invoices`,
      formData,
      {
        signal: abortSignal,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function updateInvoiceStatus(
  costId: string,
  invoiceNumber: string,
  status: CostInvoiceStatus,
  abortSignal?: AbortSignal,
): Promise<void> {
  try {
    const encodedInvoiceNumber = btoa(encodeURIComponent(invoiceNumber));
    const body: UpdateCostInvoiceStatusRequest = { status };
    await przezmorzeApi.put(`/costs/${encodeURIComponent(costId)}/invoices/${encodedInvoiceNumber}/status`, body, {
      signal: abortSignal,
    });
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function deleteInvoice(
  costId: string,
  invoiceNumber: string,
  deleteFromAll: boolean,
  abortSignal?: AbortSignal,
): Promise<void> {
  try {
    const encodedInvoiceNumber = btoa(encodeURIComponent(invoiceNumber));
    await przezmorzeApi.delete(`/costs/${encodeURIComponent(costId)}/invoices/${encodedInvoiceNumber}`, {
      signal: abortSignal,
      params: { fromAll: deleteFromAll },
    });
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 409) {
      throw new InvoiceAttachedToManyCostsError(error.response.data.message);
    } else {
      throw error;
    }
  }
}

export async function updateInvoicesExchangeRate(costId: string, abortSignal?: AbortSignal) {
  try {
    const response = await przezmorzeApi.put<AddCostInvoiceResponseListResponse>(
      `/costs/${encodeURIComponent(costId)}/invoices/updateExchangeRates`,
      {
        signal: abortSignal,
      },
    );
    return response.data.items;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function addAttachment(costId: string, body: FormData, abortSignal?: AbortSignal) {
  const response = await przezmorzeApi.post<CostFile>(`/costs/${encodeURIComponent(costId)}/attachments`, body, {
    signal: abortSignal,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
}

export async function deleteAttachment(
  costId: string,
  attachmentName: string,
  abortSignal?: AbortSignal,
): Promise<CostFile[]> {
  const encodedName = btoa(encodeURIComponent(attachmentName));
  const response = await przezmorzeApi.delete<DeleteCostAttachmentResponse>(
    `/costs/${encodeURIComponent(costId)}/attachments/${encodedName}`,
    {
      signal: abortSignal,
    },
  );
  return response.data.items;
}
